import { Helmet } from "react-helmet-async";
import { styled, useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  Container,
  Link,
  Grid,
  List,
  ListItem,
} from "@mui/material";

import Logo from "../components/Logo";
import SimpleFooter from "../components/SimpleFooter";

const StyledPageContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
});

const ContentWrapper = styled("div")(({ theme }) => ({
  flexGrow: 1,
  display: "flex",
  flexDirection: "column",
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(8),
}));

const sections = [
  {
    id: "introduction",
    title: "Introduction",
    content: (
      <Typography variant="body1" paragraph>
        Welcome to Contactie
        <Box component="sup" sx={{ fontSize: "0.6em", verticalAlign: "super" }}>
          TM
        </Box>{" "}
        Privacy Policy (Beta). We are committed to protecting your privacy. This
        Privacy Policy explains how we collect, use, and safeguard your
        information when you use our Service. By using the Service, you agree to
        the collection and use of information in accordance with this policy.
      </Typography>
    ),
  },
  {
    id: "definitions",
    title: "1. Definitions",
    content: (
      <Box sx={{ pl: 2 }}>
        <Typography variant="body1" paragraph>
          <strong>Company</strong> – refers to Contactie
          <Box
            component="sup"
            sx={{ fontSize: "0.6em", verticalAlign: "super" }}
          >
            TM
          </Box>
          , also referred to as “We”, “Us”, or “Our”.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Personal Data</strong> – any information that relates to an
          identified or identifiable individual, such as a name or email
          address.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Service</strong> – the Contactie
          <Box
            component="sup"
            sx={{ fontSize: "0.6em", verticalAlign: "super" }}
          >
            TM
          </Box>{" "}
          website and platform.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Usage Data</strong> – data collected automatically from your
          interaction with the Service.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>You</strong> – the individual accessing or using the Service.
        </Typography>
      </Box>
    ),
  },
  {
    id: "collection",
    title: "2. Information We Collect",
    content: (
      <>
        <Typography
          variant="h6"
          component="h3"
          sx={{ fontWeight: 600, mt: 2, mb: 1 }}
        >
          Personal Data You Provide to Us
        </Typography>
        <Typography variant="body1" paragraph>
          When you create an account, we require you to provide certain Personal
          Data, including:
        </Typography>
        <List sx={{ listStyleType: "disc", pl: 4 }}>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>Username</ListItem>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            First and Last Name
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            Email Address
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            Phone Number
          </ListItem>
        </List>
        <Typography variant="body1" paragraph sx={{ mt: 2 }}>
          You may also choose to provide public information on your profile,
          such as a profile picture, social media links, and your user rating.
          This information is optional and will be visible to other users.
        </Typography>

        <Typography
          variant="h6"
          component="h3"
          sx={{ fontWeight: 600, mt: 3, mb: 1 }}
        >
          Usage Data Collected Automatically
        </Typography>
        <Typography variant="body1" paragraph>
          We do not use third-party analytics tools like Google Analytics.
          Instead, we use our own internal code to collect basic Usage Data to
          understand how our Service is used. This data includes your IP
          address, approximate location based on your IP, the pages you visit on
          our Service, and the links you click.
        </Typography>

        <Typography
          variant="h6"
          component="h3"
          sx={{ fontWeight: 600, mt: 3, mb: 1 }}
        >
          Cookies
        </Typography>
        <Typography variant="body1" paragraph>
          We use a single type of essential cookie to manage your session and
          keep you logged in to your account. We do not use cookies for tracking
          or advertising purposes.
        </Typography>
      </>
    ),
  },
  {
    id: "use-of-data",
    title: "3. How We Use Your Information",
    content: (
      <>
        <Typography variant="body1" paragraph>
          We use the information we collect for the following purposes:
        </Typography>
        <List sx={{ listStyleType: "disc", pl: 4 }}>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            To provide, operate, and maintain our Service.
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            To manage Your Account and your registration as a user.
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            To contact You with essential service-related updates, security
            alerts, or support messages. We do not send marketing newsletters.
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            To respond to your feedback, questions, and support requests.
          </ListItem>
        </List>
      </>
    ),
  },
  {
    id: "sharing",
    title: "4. Sharing and Disclosure of Information",
    content: (
      <>
        <Typography variant="body1" paragraph>
          <strong>
            We do not sell, rent, or share your Personal Data with third parties
            for their marketing purposes.
          </strong>
        </Typography>
        <Typography variant="body1" paragraph>
          We may disclose your information only in the following limited
          circumstances:
        </Typography>
        <List sx={{ listStyleType: "disc", pl: 4 }}>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            <strong>To Comply with Laws:</strong> If we are required to do so by
            law, subpoena, or in response to a valid request by public
            authorities.
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0.5 }}>
            <strong>Business Transfers:</strong> If we are involved in a merger,
            acquisition, or asset sale, your Personal Data may be transferred.
            We will provide notice before this happens.
          </ListItem>
        </List>
      </>
    ),
  },
  {
    id: "transfer",
    title: "5. Transfer of Your Data",
    content: (
      <Typography variant="body1" paragraph>
        Your information, including Personal Data, is processed and stored on
        our secure servers located in Strasbourg, France. By using the Service,
        you consent to the transfer of your information to France and its
        processing there.
      </Typography>
    ),
  },
  {
    id: "rights",
    title: "6. Your Data Rights and Choices",
    content: (
      <>
        <Typography variant="body1" paragraph>
          <strong>Access and Update:</strong> You can review and update your
          public profile information in your "Account Studio" and your private
          data in your "Settings" page at any time.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Account Deletion:</strong> You may request the deletion of
          your account and all associated Personal Data by sending an email to{" "}
          <Link href="mailto:contact@contactie.com">contact@contactie.com</Link>
          . Upon receiving your request, your data will be deleted immediately
          from our active systems.
        </Typography>
      </>
    ),
  },
  {
    id: "security",
    title: "7. Security of Your Data",
    content: (
      <Typography variant="body1" paragraph>
        The security of your data is a priority for us. We use industry-standard
        security measures, including SSL/HTTPS to encrypt all data transmitted
        between your device and our servers, and we hash all user passwords.
        However, please remember that no method of transmission over the
        Internet or electronic storage is 100% secure.
      </Typography>
    ),
  },
  {
    id: "children",
    title: "8. Children's Privacy",
    content: (
      <Typography variant="body1" paragraph>
        Our Service is not intended for anyone under the age of 18. We do not
        knowingly collect personally identifiable information from anyone under
        18. If you are a parent or guardian and you believe your child has
        provided us with Personal Data, please contact us immediately.
      </Typography>
    ),
  },
  {
    id: "links",
    title: "9. Links to Other Websites",
    content: (
      <Typography variant="body1" paragraph>
        Our Service may contain links to other websites that are not operated by
        us. If you click on a third-party link, you will be directed to that
        third party's site. We have no control over and assume no responsibility
        for the content or privacy practices of any third-party sites or
        services.
      </Typography>
    ),
  },
  {
    id: "changes",
    title: "10. Changes to This Privacy Policy",
    content: (
      <Typography variant="body1" paragraph>
        We may update our Privacy Policy from time to time. We will notify you
        of any changes by posting the new Privacy Policy on this page and
        updating the "Last Updated" date. You are advised to review this page
        periodically for any changes.
      </Typography>
    ),
  },
  {
    id: "contact",
    title: "11. Contact Us",
    content: (
      <Typography variant="body1" paragraph>
        If you have any questions about this Privacy Policy, please contact us
        at:{" "}
        <Link href="mailto:contact@contactie.com">contact@contactie.com</Link>
      </Typography>
    ),
  },
];

export default function PrivacyPolicyPage() {
  const theme = useTheme();

  return (
    <>
      <Helmet>
        <title>Privacy Policy | Contactie™</title>
        <meta
          name="description"
          content="Read the official Privacy Policy for the Contactie™ platform. Understand what data we collect and how we use it to provide our service."
        />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <StyledPageContainer>
        <Box
          component="header"
          sx={{ py: 3, px: 5, display: { xs: "none", md: "flex" } }}
        >
          <Logo sx={{ height: 40, width: "auto" }} />
        </Box>

        <ContentWrapper>
          <Container maxWidth="lg">
            <Box sx={{ mb: 5, textAlign: { xs: "center", md: "left" } }}>
              <Box
                sx={{
                  mb: 5,
                  display: { xs: "flex", md: "none" },
                  justifyContent: "center",
                }}
              >
                <Logo sx={{ height: 40, width: "auto" }} />
              </Box>
              <Typography variant="h2" component="h1" sx={{ fontWeight: 700 }}>
                Contactie
                <Box
                  component="sup"
                  sx={{ fontSize: "0.6em", verticalAlign: "super" }}
                >
                  TM
                </Box>{" "}
                Privacy Policy (Beta)
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                Last Updated:{" "}
                {new Date().toLocaleString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </Typography>
            </Box>

            <Grid container spacing={5}>
              <Grid item xs={12} md={8}>
                {sections.map((section) => (
                  <Box key={section.id} id={section.id} sx={{ mb: 5 }}>
                    <Typography
                      variant="h4"
                      component="h2"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      {section.title}
                    </Typography>
                    {section.content}
                  </Box>
                ))}
              </Grid>

              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    position: "sticky",
                    top: theme.spacing(4),
                    display: { xs: "none", md: "block" },
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    On this page
                  </Typography>
                  <List dense>
                    {sections.map((section) => (
                      <ListItem key={section.id} disablePadding>
                        <Link
                          href={`#${section.id}`}
                          underline="hover"
                          sx={{ color: "text.secondary", py: 0.5 }}
                        >
                          {section.title}
                        </Link>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </ContentWrapper>
        <SimpleFooter />
      </StyledPageContainer>
    </>
  );
}
